// Internal: the demo-wall generator (PLAN.md "Sell it"). Only for Vikba ApS.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, api, copyText, del, post } from "../api";
import { AdminNav } from "./AdminBlog";

interface Demos {
  demos: {
    id: string;
    name: string;
    website: string | null;
    link: string;
    reportLink: string;
    active: boolean;
    hasReport: boolean;
    site: "none" | "working" | "ready" | "failed";
    siteError: string | null;
    shots: { desktop: boolean; phone: boolean };
    shotsPage: string;
    expiresAt: string;
    hashtags: string[];
    posts: number;
    collected: number;
    /** Posts Claude added through the admin MCP (D-085, D-089). */
    aiPosts: { own: number; customer: number; waiting: boolean } | null;
  }[];
  sources: { id: string; name: string; username: string }[];
}

export function AdminDemos() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["demos"],
    queryFn: () => api<Demos>("/v1/admin/demos"),
    refetchInterval: 15_000,
  });
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [instagram, setInstagram] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [source, setSource] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (list.error)
    return (
      <div className="onb">
        {list.error instanceof ApiError && list.error.status === 404
          ? "Page not found"
          : list.error.message}
      </div>
    );
  const d = list.data;
  const again = async (id: string, what: "site" | "collect") => {
    const r = await post<{ summary: string }>(`/v1/admin/demos/${id}/${what}`);
    setMsg(r.summary);
    qc.invalidateQueries({ queryKey: ["demos"] });
  };
  const create = async () => {
    setError(null);
    try {
      const r = await post<{ summary: string; link: string }>("/v1/admin/demos", {
        name,
        website: website || undefined,
        instagram: instagram || undefined,
        hashtags: hashtags
          .split(/[\s,]+/)
          .map((h) => h.trim())
          .filter(Boolean),
        sourceWorkspaceId: source || undefined,
      });
      setMsg(`${r.summary} ${r.link}`);
      setName("");
      setWebsite("");
      setInstagram("");
      setHashtags("");
      qc.invalidateQueries({ queryKey: ["demos"] });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };
  return (
    <div className="content" style={{ margin: "0 auto" }}>
      <div className="stack" style={{ gap: 18 }}>
        <AdminNav here="demos" />
        <h1>Demo walls</h1>
        <p className="muted">
          A private, unlisted demo of a brand's wall, from its public Instagram and hashtags, with a
          report you can send. Clearly marked as a demo, never suggests a partnership. Paused after
          30 days; turn it on again any time.
        </p>
        <section className="card stack">
          {error && <div className="error">{error}</div>}
          {msg && (
            <div className="notice" style={{ wordBreak: "break-all" }}>
              {msg}
            </div>
          )}
          <div className="grid2" style={{ gap: 12 }}>
            <label className="field">
              Brand
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Norrøna"
              />
            </label>
            <label className="field">
              Website
              <input
                className="input"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="norrona.com"
              />
            </label>
            <label className="field">
              Their Instagram
              <input
                className="input"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                placeholder="@norrona"
              />
            </label>
            <label className="field">
              Hashtags (up to 5)
              <input
                className="input"
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                placeholder="#norrona #norronalife"
              />
            </label>
            {d && d.sources.length > 0 && (
              <label className="field">
                Who finds the posts
                <select
                  className="input"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                >
                  <option value="">Claude, in Claude Cowork</option>
                  {d.sources.map((s) => (
                    <option key={s.id} value={s.id}>
                      The Instagram account of {s.name} (@{s.username})
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {!source && (
            <p className="small muted">
              The demo waits for its posts. Claude finds the brand's customer posts on Instagram,
              adds them through the admin MCP (add_posts) and builds the demo (build_demo). Each
              demo also gets a report on what customer posts could do for the brand.
            </p>
          )}
          <button
            type="button"
            className="btn"
            style={{ justifySelf: "start" }}
            disabled={!name.trim()}
            onClick={create}
          >
            Make the demo
          </button>
        </section>
        <section className="card stack">
          <h2>Your demos</h2>
          {d?.demos.length === 0 && <p className="muted">None yet.</p>}
          {d?.demos.map((x) => (
            <div key={x.id} className="row wrap">
              <span className="spacer">
                <b>{x.name}</b>{" "}
                <span className={`pill ${x.active ? "ok" : "warn"}`}>
                  {x.active ? "Active" : "Paused"}
                </span>{" "}
                {x.aiPosts?.waiting ? (
                  <span className="pill">
                    Waiting for posts from AI ({x.aiPosts.customer} added)
                  </span>
                ) : (
                  !x.hasReport && <span className="pill">Building…</span>
                )}{" "}
                <span className="small muted">
                  {x.posts} on the wall, {x.collected} collected ·{" "}
                  {x.hashtags.map((h) => `#${h}`).join(" ")} · {x.active ? "until" : "since"}{" "}
                  {new Date(x.expiresAt).toLocaleDateString("en-GB")}
                  {x.site === "working" && " · no homepage pictures yet"}
                  {x.site === "ready" && " · on their homepage"}
                </span>
                {x.site === "failed" && (
                  <span className="small error" style={{ display: "block" }}>
                    Their homepage couldn't be opened, so the demo uses a plain page. {x.siteError}
                  </span>
                )}
              </span>
              <button
                type="button"
                className="btn secondary sm"
                onClick={async () => {
                  await post(`/v1/admin/demos/${x.id}/${x.active ? "deactivate" : "activate"}`);
                  qc.invalidateQueries({ queryKey: ["demos"] });
                }}
              >
                {x.active ? "Pause" : "Activate again (30 days)"}
              </button>
              <a className="btn secondary sm" href={x.link} target="_blank" rel="noreferrer">
                Open
              </a>
              {x.site !== "none" && (
                <button
                  type="button"
                  className="btn secondary sm"
                  onClick={() => again(x.id, "site")}
                >
                  New homepage pictures
                </button>
              )}
              {(x.hasReport || (x.aiPosts?.waiting && x.aiPosts.customer > 0)) && (
                <button
                  type="button"
                  className="btn secondary sm"
                  onClick={() => again(x.id, "collect")}
                >
                  {!x.aiPosts
                    ? "Let Claude add the posts"
                    : x.aiPosts.waiting
                      ? "Build"
                      : "Build again"}
                </button>
              )}
              {x.hasReport && (
                <a
                  className="btn secondary sm"
                  href={x.reportLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Report
                </a>
              )}
              <a className="btn secondary sm" href={x.shotsPage}>
                Screenshots{x.shots.desktop || x.shots.phone ? " ✓" : ""}
              </a>
              <button type="button" className="btn secondary sm" onClick={() => copyText(x.link)}>
                Copy link
              </button>
              <button
                type="button"
                className="btn link small"
                onClick={async () => {
                  await del(`/v1/admin/demos/${x.id}`);
                  qc.invalidateQueries({ queryKey: ["demos"] });
                }}
              >
                Delete
              </button>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
